import { useMemo } from "react";
import { format, addDays, isSameDay } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface CalendarEvent {
  id: string;
  title: string;
  subtitle?: string;
  date: Date;
  startHour: number;
  startMinute: number;
  durationMinutes: number;
  color: string;
  type: "session" | "booking";
}

interface WeekCalendarProps {
  events: CalendarEvent[];
  weekStart: Date;
  startHour?: number;
  endHour?: number;
  onEventClick?: (event: CalendarEvent) => void;
}

const SLOT_HEIGHT = 48; // px per 30 min

export default function WeekCalendar({
  events,
  weekStart,
  startHour = 6,
  endHour = 24,
  onEventClick,
}: WeekCalendarProps) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const totalSlots = (endHour - startHour) * 2;
  const timeLabels = useMemo(
    () =>
      Array.from({ length: totalSlots }, (_, i) => {
        const h = startHour + Math.floor(i / 2);
        const m = (i % 2) * 30;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      }),
    [startHour, totalSlots]
  );

  const getEventStyle = (ev: CalendarEvent) => {
    const topSlots = (ev.startHour - startHour) * 2 + ev.startMinute / 30;
    const heightSlots = ev.durationMinutes / 30;
    return {
      top: `${topSlots * SLOT_HEIGHT}px`,
      height: `${Math.max(heightSlots * SLOT_HEIGHT - 2, SLOT_HEIGHT / 2)}px`,
    };
  };

  const today = new Date();

  return (
    <ScrollArea className="h-[calc(100vh-220px)] rounded-lg border bg-card">
      <div className="flex min-w-[700px]">
        {/* Time column */}
        <div className="w-14 shrink-0 border-r">
          <div className="h-10 border-b" />
          {timeLabels.map((label, i) => (
            <div
              key={i}
              className="flex h-12 items-start justify-end pr-2 text-[10px] text-muted-foreground"
            >
              {i % 2 === 0 ? label : ""}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map((day, di) => {
          const dayEvents = events.filter((e) => isSameDay(e.date, day));
          const isToday = isSameDay(day, today);
          return (
            <div key={di} className="flex-1 min-w-[90px] border-r last:border-r-0">
              {/* Header */}
              <div
                className={cn(
                  "flex h-10 flex-col items-center justify-center border-b text-xs",
                  isToday && "bg-primary/10 font-semibold text-primary"
                )}
              >
                <span className="capitalize">
                  {format(day, "EEE", { locale: sv })}
                </span>
                <span>{format(day, "d/M")}</span>
              </div>

              {/* Slots */}
              <div className="relative">
                {timeLabels.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-12 border-b border-dashed border-muted/40",
                      i % 2 === 0 && "border-muted"
                    )}
                  />
                ))}

                {/* Events */}
                {dayEvents.map((ev) => (
                  <button
                    key={ev.id}
                    className="absolute inset-x-1 z-10 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm transition-opacity hover:opacity-90"
                    style={{
                      ...getEventStyle(ev),
                      backgroundColor: ev.type === "booking" ? "hsl(var(--muted))" : ev.color + "cc",
                      color: ev.type === "booking" ? "hsl(var(--muted-foreground))" : "#fff",
                    }}
                    onClick={() => onEventClick?.(ev)}
                  >
                    <span className="font-medium line-clamp-1">{ev.title}</span>
                    {ev.subtitle && (
                      <span className="line-clamp-1 opacity-80">{ev.subtitle}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
